
/**
 * Utility functions for managing cached data with TTL (Time To Live)
 */

/**
 * Store data in local storage with timestamp
 * @param key Cache key
 * @param data Data to store
 * @param ttlMs Time to live in milliseconds
 */
export const setCachedData = <T>(key: string, data: T, ttlMs: number = 5 * 60 * 1000): void => {
  try {
    localStorage.setItem(key, JSON.stringify({
      data,
      timestamp: Date.now()
    }));
  } catch (error) {
    console.error(`Error caching data for ${key}:`, error);
  }
};

/**
 * Get cached data if it exists and is not expired
 * @param key Cache key
 * @param ttlMs Time to live in milliseconds
 * @returns The cached data or null if expired or not found
 */
export const getCachedData = <T>(key: string, ttlMs: number = 5 * 60 * 1000): T | null => {
  try {
    const cached = localStorage.getItem(key);
    if (!cached) return null;
    
    const { data, timestamp } = JSON.parse(cached);
    
    // Return data if cache is still valid
    if (Date.now() - timestamp < ttlMs) {
      return data as T;
    }
    
    // Clear expired cache
    localStorage.removeItem(key);
    return null;
  } catch (error) {
    console.error(`Error reading cached data for ${key}:`, error);
    return null;
  }
};

/**
 * Remove cached data
 * @param key Cache key
 */
export const removeCachedData = (key: string): void => {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.error(`Error removing cached data for ${key}:`, error);
  }
};
